import { SEED_IMAGES, type IrishCounty } from "@dc-ireland/shared"

export type ChapterStatus = "live" | "coming-soon"

export type ChapterSocialBrand = "youtube" | "instagram" | "facebook" | "tiktok" | "x"

/** Optional chapter social links — only platforms with a URL are shown in the hero. */
export type ChapterSocials = Partial<Record<ChapterSocialBrand, string>>

export type ChapterMeta = {
  slug: string
  name: string
  status: ChapterStatus
  tagline: string
  heroImage: string
  socials?: ChapterSocials
}

const SOCIAL_ORDER: ChapterSocialBrand[] = ["youtube", "instagram", "facebook", "tiktok", "x"]

export function getChapterSocialIcons(
  socials?: ChapterSocials
): { brand: ChapterSocialBrand; href: string }[] {
  if (!socials) return []
  return SOCIAL_ORDER.flatMap((key) => {
    const href = socials[key]?.trim()
    return href ? [{ brand: key, href }] : []
  })
}

export const LIVE_CHAPTER_SLUGS = ["dublin", "cork", "drogheda", "kerry", "sligo", "waterford"] as const
export const COMING_SOON_CHAPTER_SLUGS = [] as const

export const CHAPTER_REGISTRY: ChapterMeta[] = [
  {
    slug: "dublin",
    name: "Dublin",
    status: "live",
    tagline: "Our headquarters — worship, community, and kingdom impact in the heart of Ireland.",
    heroImage: SEED_IMAGES.chapters.dublin.hero,
    socials: {
      youtube: "https://www.youtube.com/@dominioncitydublin",
      instagram: "https://www.instagram.com/dominion_city_dublin",
      facebook: "https://www.facebook.com/profile.php?id=100079498972817",
      tiktok: "https://www.tiktok.com/@dominioncitydublin",
      x: "https://www.x.com/dc_dublin",
    },
  },
  {
    slug: "cork",
    name: "Cork",
    status: "live",
    tagline: "A vibrant community of believers growing together in faith across Munster.",
    heroImage: SEED_IMAGES.chapters.cork.hero,
    socials: {
      instagram: "https://www.instagram.com/dominioncitycork",
      facebook: "https://www.facebook.com/profile.php?id=100090400680485",
    },
  },
  {
    slug: "drogheda",
    name: "Drogheda",
    status: "live",
    tagline: "Something beautiful is taking root in the Boyne Valley.",
    heroImage: SEED_IMAGES.chapters.drogheda.hero,
  },
  {
    slug: "kerry",
    name: "Kerry",
    status: "live",
    tagline: "Faith, family, and fellowship in the Kingdom of Kerry.",
    heroImage: SEED_IMAGES.chapters.kerry.hero,
  },
  {
    slug: "sligo",
    name: "Sligo",
    status: "live",
    tagline: "A new chapter of worship and fellowship on the Wild Atlantic Way.",
    heroImage: SEED_IMAGES.chapters.sligo.hero,
  },
  {
    slug: "waterford",
    name: "Waterford",
    status: "live",
    tagline: "Building a house of praise in Ireland's oldest city.",
    heroImage: SEED_IMAGES.chapters.waterford.hero,
  },
]

export const CHAPTER_CONTACT_EMAILS = {
  to: "info@dominioncity.ie",
  cc: "admin@dominioncity.ie",
} as const

export const chapterSectionContainerClass =
  "mx-auto flex w-full max-w-6xl flex-col items-center px-4 md:px-8 lg:px-20"

export const chapterSectionContentClass = "mt-12 w-full md:mt-16"

export const CHAPTER_EVENT_SLUGS = [
  "dublin",
  "cork",
  "drogheda",
  "kerry",
  "sligo",
  "waterford",
  "external",
] as const

/** County a chapter page should preselect on prayer and testimony forms. */
export const CHAPTER_COUNTY: Record<string, IrishCounty> = {
  dublin: "Dublin",
  cork: "Cork",
  drogheda: "Louth",
  kerry: "Kerry",
  sligo: "Sligo",
  waterford: "Waterford",
}

export function getChapterMeta(slug: string): ChapterMeta | undefined {
  return CHAPTER_REGISTRY.find((chapter) => chapter.slug === slug)
}

export function isLiveChapter(slug: string): boolean {
  return LIVE_CHAPTER_SLUGS.includes(slug as (typeof LIVE_CHAPTER_SLUGS)[number])
}

export function chapterPath(slug: string): string {
  return `/${slug}`
}

export function chapterGallerySrc(chapterSlug: string, filename: string): string {
  return `/gallery/${chapterSlug}/${filename}`
}

export function chapterGalleryFolder(chapterSlug: string): string {
  return `gallery/${chapterSlug}`
}

export function getActiveChapterSlug(pathname: string): string | undefined {
  if (pathname.startsWith("/chapters/")) {
    const legacySlug = pathname.replace("/chapters/", "").split("/")[0]
    return getChapterMeta(legacySlug) ? legacySlug : undefined
  }

  const slug = pathname.replace(/^\//, "").split("/")[0]
  return slug && getChapterMeta(slug) ? slug : undefined
}

export function isChaptersNavActive(pathname: string): boolean {
  return Boolean(getActiveChapterSlug(pathname))
}

export function buildDirectionsUrl(address: string[]): string {
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address.join(", "))}`
}

export function isPhysicalAddress(address: string[]) {
  if (address.length === 0) return false
  return !address.some((line) => /online|zoom/i.test(line))
}

export function parseChapterServiceTimes(times: string[]) {
  const sundayTime = times.find((time) => /sunday/i.test(time))
  const midweekTime = times.find((time) => !/sunday/i.test(time))
  return { sundayTime, midweekTime }
}

export function buildInterestMailto(chapterName: string, cellGroup?: string): string {
  const subject = encodeURIComponent(
    cellGroup ? `${chapterName} - ${cellGroup} interest` : `${chapterName} - Cell group interest`
  )
  return `mailto:${CHAPTER_CONTACT_EMAILS.to}?subject=${subject}`
}
